'use client';

import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Grid } from '@mui/material';
import {
	DataGridPro,
	GridExpandMoreIcon,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
	getGridStringOperators,
} from '@mui/x-data-grid-pro';
import { useLazyQuery, useQuery } from '@parkchirp/api-client';
import { isEmptyGridFilterValue } from '@parkchirp/ui-library';
import { camelCase } from 'lodash';
import Papa from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ParkerAccessDeviceTypeDisplayEnum } from '../../enums';
import { useDebounce } from '../../hooks/useDebounce';
import {
	GET_PARKER_ACCESS_DEVICES_CSV,
	GET_PRESIGNED_URL,
	GET_PUT_SIGNED_URL,
	LIST_PARKER_ACCESS_DEVICES_COUNT,
	LIST_PARKERS_ACCESS_DEVICES,
} from '../../queries';
import { DownloadReport, SetShowDialog, SetShowSnackbar } from '../../types';
import { convertMuiFilterForV2Api, datagridFilterPanelAttributes, formatDateForDisplay } from '../../utils';
import { FacilityAutocomplete, InputFile } from '../inputs';
import { DatagridLinearProgress } from '../loading';
import { SimpleModal } from '../modals';
import { CsvExportButtonV2 } from '../reports';
import { ParkerDetailModal } from './parkerDetail';

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
};

const numericOperators = getGridNumericOperators();
const dateOperators = getGridDateOperators();
const stringOperators = getGridStringOperators();

function capitalizeFirstLetter(str: string) {
	switch (str) {
		case null: {
			return '';
		}
		case 'termination_pending': {
			const splitStr = str.split('_');
			const string1 = splitStr[0].charAt(0).toUpperCase() + splitStr[0].slice(1);
			const string2 = splitStr[1].charAt(0).toUpperCase() + splitStr[1].slice(1);
			const final = `${string1} ${string2}`;
			return final;
		}
		default:
			return str.charAt(0).toUpperCase() + str.slice(1);
	}
}

type AccessDevicesTableProps = {
	tabNav?: React.JSX.Element;
	customFilters?: { field: string; op: string; value: any }[];
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	theme;
	tenantSubscriptionId?: string;
	facilityID?: string;
	tenantAccountId?: number;
	userRole?: string;
	organizationId?: number | null;
	setOrganizationId?: (id: number | null) => void;
	isAdminDashboard?: boolean;
	downloadReport?: DownloadReport;
};

type AccessDeviceAuditRow = {
	firstName: string;
	lastName: string;
	accessDeviceId: string;
};

const AccessDeviceAuditTemplateButton = ({ setShowSnackbar }) => {
	const [isDownloading, setIsDownloading] = useState(false);

	const [getPresignedUrl] = useLazyQuery(GET_PRESIGNED_URL);

	const downloadAuditTemplate = async () => {
		setIsDownloading(true);

		try {
			const accessDeviceAuditTemplateUrl = await getPresignedUrl({
				variables: {
					bucket: process.env.NEXT_PUBLIC_ACCESS_DEVICE_AUDIT_S3_BUCKET,
					s3Key: `templates/Access Device Audit Template.csv`,
				},
				context: {
					apiVersion: 'v2',
				},
			});

			window.open(accessDeviceAuditTemplateUrl.data.getPresignedUrl.presignedUrl);
		} catch (error) {
			setShowSnackbar({
				message: error.message,
				severity: 'error',
				autoHideDuration: 8000,
			});
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<Button onClick={() => downloadAuditTemplate()}>
			<span style={{ marginRight: '5px' }}>Access Device Audit Template</span>
			{isDownloading ? (
				<CircularProgress style={{ height: '20px', width: '20px' }} />
			) : (
				<DownloadIcon style={{ height: '20px', width: '20px' }} />
			)}
		</Button>
	);
};

const AccessDeviceAuditModal = ({
	setShowDialog,
	setShowSnackbar,
	userRole,
	organizationId,
	setOrganizationId,
	downloadReport,
}) => {
	const [isUploadingFile, setIsUploadingFile] = useState(false);
	const [file, setFile] = useState<File>(null);
	const [selectedFacility, setSelectedFacility] = useState<{
		facilityId: string;
		facilityNumber: string;
		facilityName: string;
		integration: string | null;
	}>(null);
	const [isDownloadingXlsx, setIsDownloadingXlsx] = useState<boolean>(false);

	const [getPutSignedUrl] = useLazyQuery(GET_PUT_SIGNED_URL);

	const handleFileChange = useCallback((_, e) => {
		if (!e.target.files) return;
		const [fileData] = Array.from(e.target.files) as File[];
		setFile(fileData);
	}, []);

	const validateAccessDeviceAuditHeaders = (headers: string[]) => {
		if (headers.length !== 3) return false;
		if (headers[0] !== 'firstName') return false;
		if (headers[1] !== 'lastName') return false;
		if (headers[2] !== 'accessDeviceId') return false;

		return true;
	};

	const validateAccessDeviceAuditRows = (rows: AccessDeviceAuditRow[]) => {
		const invalidRows = Array.from(rows.entries())
			.filter(([_, row]) => !row.accessDeviceId || row.accessDeviceId.trim() === '')
			.map(([idx, _]) => idx + 1);

		return invalidRows;
	};

	const handleUpload = async () => {
		setIsUploadingFile(true);
		try {
			Papa.parse(file, {
				header: true,
				skipEmptyLines: true,
				transformHeader: (header: string) => camelCase(header),
				complete: async (results: { meta: any; data: any[] }) => {
					const { fields } = results.meta;
					const isValidHeaders = validateAccessDeviceAuditHeaders(fields);

					if (!isValidHeaders) {
						setIsUploadingFile(false);
						setShowDialog(null);
						setShowDialog(
							<SimpleModal
								confirmButtonInTopRightOfHeader
								title='File Upload Failed Validation'
								content={
									<div style={{ textAlign: 'center' }}>
										Invalid file format. Please use the Access Device Audit Template.
									</div>
								}
								contentStyle={{ margin: '10px 0', padding: '0 30px' }}
								handleConfirm={() => setShowDialog(null)}
								confirmText='OK'
							/>,
						);

						return;
					}

					const invalidRows = validateAccessDeviceAuditRows(results.data);

					if (invalidRows.length > 0) {
						setIsUploadingFile(false);
						setShowDialog(null);
						setShowDialog(
							<SimpleModal
								confirmButtonInTopRightOfHeader
								title='File Upload Failed Validation'
								content={
									<div>
										<Accordion>
											<AccordionSummary expandIcon={<GridExpandMoreIcon />}>
												The following rows are missing an Access Device ID, or are blank:
											</AccordionSummary>
											<AccordionDetails>
												{invalidRows.map((rowNumber) => (
													<div key={rowNumber}>Row #{rowNumber}</div>
												))}
											</AccordionDetails>
										</Accordion>
									</div>
								}
								contentStyle={{ margin: '10px 0', padding: '0 30px' }}
								handleConfirm={() => setShowDialog(null)}
								confirmText='OK'
							/>,
						);

						return;
					}

					try {
						const uploadTimestamp = Date.now();
						const fileName = `${uploadTimestamp}-${file.name}`;

						const putSignedUrlResponse = await getPutSignedUrl({
							variables: {
								bucket: process.env.NEXT_PUBLIC_ACCESS_DEVICE_AUDIT_S3_BUCKET,
								s3Key: fileName,
							},
							context: {
								apiVersion: 'v2',
							},
						});

						const putSignedUrl = putSignedUrlResponse.data.getPutSignedUrl.url;
						await fetch(putSignedUrl, {
							method: 'PUT',
							headers: { 'Content-Type': file.type },
							body: file,
						});

						setIsUploadingFile(false);

						await downloadReport('xlsx', {
							reportType: 'AccessDeviceAuditReport',
							params: {
								facilityId: selectedFacility.facilityId,
								parkers: results.data,
								deviceIds: results.data.map((row) => row.accessDeviceId.trim()),
							},
							handleValidation: () => true,
							setIsDownloadingXlsx,
						});

						setShowSnackbar({
							message: 'Audit has been created. Please check your downloads to review the audit file.',
							severity: 'success',
							autoHideDuration: null,
						});
					} catch (error) {
						setShowSnackbar({
							message: error.message,
							severity: 'error',
							autoHideDuration: 8000,
						});
					} finally {
						setIsUploadingFile(false);
						setShowDialog(null);
					}
				},
			});
		} catch (error) {
			setShowSnackbar({
				message: error.message,
				severity: 'error',
				autoHideDuration: 8000,
			});

			setIsUploadingFile(false);
			setShowDialog(null);
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<h3>Upload Access Device Audit</h3>
			</Grid>
			<Grid item xs={12}>
				<FacilityAutocomplete
					disabled={isUploadingFile}
					facilityId={selectedFacility?.facilityId}
					facilityName={selectedFacility?.facilityName}
					facilityNumber={selectedFacility?.facilityNumber}
					integration={selectedFacility?.integration}
					userRole={userRole}
					organizationId={organizationId}
					setOrganizationId={setOrganizationId}
					label='Search Facility'
					onChange={(facility) => {
						if (facility) {
							const { id: facilityId, facilityNumber, facilityName, integration } = facility;
							setSelectedFacility({
								facilityId,
								facilityNumber: facilityNumber.toString(),
								facilityName,
								integration,
							});
						}
					}}
				/>
			</Grid>
			<Grid item xs={12}>
				<InputFile
					disabled={isUploadingFile}
					file={file}
					label='Select the Access Device Audit CSV...'
					handleFileChange={handleFileChange}
					type='accessDeviceAudit'
				/>
			</Grid>
			<Grid item xs={6}>
				<Button onClick={() => setShowDialog(null)} data-cy='cancel-button'>
					Cancel
				</Button>
			</Grid>
			<Grid item xs={6}>
				<Button
					fullWidth
					disabled={isUploadingFile || isDownloadingXlsx || !file || !selectedFacility}
					onClick={() => handleUpload()}
					data-cy='upload-button'
				>
					{isUploadingFile && 'Uploading and validating...'}
					{isDownloadingXlsx && 'Generating audit report...'}
					{!isUploadingFile && !isDownloadingXlsx && 'Upload'}
				</Button>
			</Grid>
		</Grid>
	);
};

const AccessDeviceAuditUploadButton = ({
	setShowSnackbar,
	setShowDialog,
	userRole,
	organizationId,
	setOrganizationId,
	downloadReport,
}) => (
	<Button
		onClick={() =>
			setShowDialog(
				<AccessDeviceAuditModal
					setShowSnackbar={setShowSnackbar}
					setShowDialog={setShowDialog}
					userRole={userRole}
					organizationId={organizationId}
					setOrganizationId={setOrganizationId}
					downloadReport={downloadReport}
				/>,
			)
		}
	>
		<span style={{ marginRight: '5px' }}>Access Device Audit Upload</span>
		<UploadIcon style={{ height: '20px', width: '20px' }} />
	</Button>
);

export const AccessDevicesTable = ({
	setShowDialog,
	setShowSnackbar,
	facilityID,
	tabNav,
	tenantSubscriptionId,
	tenantAccountId,
	theme,
	userRole,
	organizationId,
	setOrganizationId,
	isAdminDashboard = false,
	downloadReport,
}: AccessDevicesTableProps) => {
	const defaultFilters: any = useMemo(
		() => [
			{
				propertyName: 'suspensionStatus',
				op: 'in',
				value: ['ACTIVE', 'SUSPENSION_PENDING'],
			},
			{
				propertyName: 'terminationStatus',
				op: 'in',
				value: ['ACTIVE', 'TERMINATION_PENDING'],
			},
		],
		[],
	);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState(0);
	const [sortOrder, setSortOrder] = useState({ field: 'createdAt', sort: 'desc' });

	const [filters, setFilters] = useState(defaultFilters);
	const debouncedSetFilters = useDebounce(setFilters, 500);
	const accessDeviceFilters = useMemo(
		() => [
			...filters,
			...(facilityID
				? [
						{
							propertyName: 'facilityId',
							op: 'equals',
							value: facilityID,
						},
					]
				: []),
			...(tenantAccountId
				? [
						{
							propertyName: 'subscriptionAccountId',
							op: 'equals',
							value: tenantAccountId,
						},
					]
				: []),
		],
		[facilityID, filters, tenantAccountId],
	);

	const {
		loading: listParkerAccessDevicesLoading,
		data: listParkerAccessDevicesData,
		refetch: refetchListParkerAccessDevices,
	} = useQuery(LIST_PARKERS_ACCESS_DEVICES, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			tenantSubscriptionId,
			filters: accessDeviceFilters,
		},
		context: { apiVersion: 'v2' },
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});

	const { loading: listParkerAccessDevicesCountLoading, data: listParkerAccessDevicesCountData } = useQuery(
		LIST_PARKER_ACCESS_DEVICES_COUNT,
		{
			variables: {
				filters: accessDeviceFilters,
				tenantSubscriptionId,
			},
			context: { apiVersion: 'v2' },
			fetchPolicy: 'network-only',
		},
	);

	useEffect(() => {
		if (!listParkerAccessDevicesCountLoading && listParkerAccessDevicesCountData) {
			setRowCount(listParkerAccessDevicesCountData.listParkerAccessDevicesCount.total);
		}
	}, [listParkerAccessDevicesCountData, listParkerAccessDevicesCountLoading]);

	const deviceR = useMemo(() => {
		const skeletonOfWhenRefetching =
			listParkerAccessDevicesData?.listParkerAccessDevicesV2?.parkerAccessDevices?.[0]?.parkerId;
		if (skeletonOfWhenRefetching) {
			return listParkerAccessDevicesData?.listParkerAccessDevicesV2?.parkerAccessDevices?.map((device: any) => ({
				id: uuidv4(),
				parkerId: device.parkerId,
				suspensionStatus: device.parker.suspensionStatus,
				terminationStatus: device.parker.terminationStatus,
				firstName: capitalizeFirstLetter(device.parker.firstName),
				lastName: capitalizeFirstLetter(device.parker.lastName),
				facilityNumber: device.parker.subscription.billingProfile.facility.facilityNumber,
				facilityName: device.parker.subscription.billingProfile.facility.facilityName,
				facilityId: device.parker.subscription.billingProfile.facilityId,
				subscriptionName: device.parker.subscription.subscriptionName,
				subscriptionId: device.parker.subscription.id,
				subscriptionAccountId: device.parker.subscription.accountId,
				deviceType: ParkerAccessDeviceTypeDisplayEnum[device.accessDeviceType.type],
				deviceId: device.deviceId,
				status: device.status.toUpperCase(),
				dateAdded: formatDateForDisplay(device.dateAdded),
				dateActive: formatDateForDisplay(device.dateActive),
				dateInactive: device.dateInactive ? formatDateForDisplay(device.dateInactive) : '',
			}));
		}
		return [];
	}, [listParkerAccessDevicesData]);

	const columns = [
		{
			field: 'lastName',
			headerName: 'Last Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() =>
						setShowDialog(
							<ParkerDetailModal
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.subscriptionAccountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								setShowSnackbar={setShowSnackbar}
								subscriptionId={params.row.subscriptionId}
								tenantSubscriptionId={tenantSubscriptionId}
								handleClose={() => {
									refetchListParkerAccessDevices();
									setShowDialog(null);
								}}
								theme={theme}
							/>,
						)
					}
				>
					{params.row.lastName}
				</a>
			),
			filterOperators: stringOperators.filter(({ value }) => !['isEmpty', 'isNotEmpty'].includes(value)),
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() =>
						setShowDialog(
							<ParkerDetailModal
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.subscriptionAccountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								setShowSnackbar={setShowSnackbar}
								subscriptionId={params.row.subscriptionId}
								tenantSubscriptionId={tenantSubscriptionId}
								handleClose={() => {
									refetchListParkerAccessDevices();
									setShowDialog(null);
								}}
								theme={theme}
							/>,
						)
					}
				>
					{params.row.firstName}
				</a>
			),
			filterOperators: stringOperators.filter(({ value }) => !['isEmpty', 'isNotEmpty'].includes(value)),
		},
		{
			field: 'suspensionStatus',
			headerName: 'Suspension Status',
			type: 'singleSelect' as const,
			width: 200,
			valueOptions: [
				{ value: 'ACTIVE', label: 'Active' },
				{ value: 'PENDING', label: 'Pending' },
				{ value: 'SUSPENSION_PENDING', label: 'Suspension Pending' },
				{ value: 'SUSPENDED', label: 'Suspended' },
				{ value: 'TERMINATED', label: 'Terminated' },
				{ value: 'NOT_INVOICED', label: 'Not Invoiced' },
			],
		},
		{
			field: 'terminationStatus',
			headerName: 'Termination Status',
			width: 200,
			type: 'singleSelect' as const,
			valueOptions: [
				{ value: 'ACTIVE', label: 'Active' },
				{ value: 'PENDING', label: 'Pending' },
				{ value: 'TERMINATION_PENDING', label: 'Termination Pending' },
				{ value: 'TERMINATED', label: 'Terminated' },
				{ value: 'NOT_INVOICED', label: 'Not Invoiced' },
			],
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			filterOperators: stringOperators.filter(({ value }) => !['isEmpty', 'isNotEmpty'].includes(value)),
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility Number',
			filterOperators: numericOperators,
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
		},
		{
			field: 'subscriptionAccountId',
			headerName: 'Subscription Account ID',
			filterOperators: numericOperators,
			renderCell: (params) => `AC-${params.row.subscriptionAccountId}`,
		},
		{
			field: 'deviceId',
			headerName: 'Device ID',
		},
		{
			field: 'deviceType',
			headerName: 'Device Type',
			type: 'singleSelect' as const,
			valueOptions: [
				{ value: 'PROXIMITY_CARD', label: 'Proximity Card' },
				{ value: 'TENANT_ACCESS_CARD', label: 'Tenant Access Card' },
				{ value: 'AVI_TRANSPONDER', label: 'AVI Transponder' },
				{ value: 'WINDSHIELD_STICKER', label: 'Windshield Sticker' },
				{ value: 'FLASH_PARKING_APP', label: 'Flash Parking App' },
				{ value: 'HANG_TAG', label: 'Hang Tag' },
				{ value: 'BARCODE', label: 'Barcode' },
				{ value: 'LICENSE_PLATE_RECOGNITION', label: 'License Plate Recognition' },
			],
		},
		{
			field: 'status',
			headerName: 'Device Status',
			type: 'singleSelect' as const,
			valueOptions: [
				{ value: 'ACTIVE', label: 'Active' },
				{ value: 'INACTIVE', label: 'Inactive' },
			],
		},
		{
			field: 'dateAdded',
			headerName: 'Date Added',
			filterOperators: dateOperators,
		},
		{
			field: 'dateActive',
			headerName: 'Active Date',
			filterOperators: dateOperators,
		},
		{
			field: 'dateInactive',
			headerName: 'Inactive Date',
			filterOperators: dateOperators,
		},
	];

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		if (!sortModel.length) {
			return setSortOrder({ field: 'id', sort: 'asc' });
		}

		const { field, sort } = sortModel[0];

		return setSortOrder({ field, sort });
	}, []);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			const { items: filterItems } = filterModel;

			if (!filterItems.length) {
				debouncedSetFilters([]);
				return;
			}

			const queryFilters = filterItems
				.map((filter) => {
					const { field, operator, value: rawValue } = filter;
					if (isEmptyGridFilterValue(rawValue) && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];

					const { op, value } = convertMuiFilterForV2Api(operator, rawValue);

					return {
						propertyName: field,
						op,
						value,
					};
				})
				.flat();
			debouncedSetFilters(queryFilters);
		},
		[debouncedSetFilters],
	);

	const renderCustomToolbar = () => (
		<GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
			<div>
				<GridToolbarFilterButton />
				<GridToolbarColumnsButton />
				<GridToolbarDensitySelector />
				<CsvExportButtonV2
					query={GET_PARKER_ACCESS_DEVICES_CSV}
					queryName='getParkerAccessDevicesCsv'
					args={{
						variables: {
							filters: [
								...filters,
								...(facilityID
									? [
											{
												propertyName: 'facilityId',
												op: 'equals',
												value: facilityID,
											},
										]
									: []),
								...(tenantAccountId
									? [
											{
												propertyName: 'subscriptionAccountId',
												op: 'equals',
												value: tenantAccountId,
											},
										]
									: []),
							],
							order: sortOrder,
							tenantSubscriptionId,
						},
						context: {
							apiVersion: 'v2',
						},
					}}
				/>
			</div>
			{isAdminDashboard && (
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<AccessDeviceAuditTemplateButton setShowSnackbar={setShowSnackbar} />
					<AccessDeviceAuditUploadButton
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
						userRole={userRole}
						organizationId={organizationId}
						setOrganizationId={setOrganizationId}
						downloadReport={downloadReport}
					/>
				</div>
			)}
		</GridToolbarContainer>
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabNav}
			<div className='pca__datagrid-sticky--headfoot' data-cy='access-devices-datagrid-container'>
				<DataGridPro
					sx={{ height: '100%' }}
					paginationModel={{ page, pageSize }}
					onSortModelChange={onSortModelChange}
					filterMode='server'
					onFilterModelChange={onFilterModelChange}
					hideFooter={false}
					hideFooterPagination={false}
					onPaginationModelChange={setPaginationModel}
					pagination
					rows={deviceR || []}
					rowCount={rowCount}
					columns={columns}
					pageSizeOptions={[pageSize]}
					paginationMode='server'
					sortingMode='server'
					loading={listParkerAccessDevicesLoading}
					slots={{
						toolbar: renderCustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
					initialState={{
						filter: {
							filterModel: {
								items: [
									{
										id: 1,
										field: 'suspensionStatus',
										operator: 'isAnyOf',
										value: ['ACTIVE', 'SUSPENSION_PENDING'],
									},
									{
										id: 2,
										field: 'terminationStatus',
										operator: 'isAnyOf',
										value: ['ACTIVE', 'TERMINATION_PENDING'],
									},
								],
							},
						},
					}}
				/>
			</div>
		</div>
	);
};
